import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Plus, Pencil, Trash2, MessageSquare, RefreshCw, X } from 'lucide-react';
import { smsTemplateApi } from '../services/api';
import { useToast } from '../components/Toast/Toast';
import { useAuth } from '../context/AuthContext';

// The wording the company sends when chasing a payment. A template is a name
// and a message; the follow-up screen fills the placeholders in from the bill
// in front of the user, so what they see in the box is what goes out.

type Template = {
  id: number; name: string; body: string; is_active: number;
  created_by: string | null; updated_by: string | null; updated_at: string | null;
};

const PLACEHOLDERS: { key: string; meaning: string }[] = [
  { key: '{party}',     meaning: "the customer's name" },
  { key: '{bill}',      meaning: 'the bill number' },
  { key: '{amount}',    meaning: 'the outstanding amount' },
  { key: '{days}',      meaning: 'how many days old the bill is' },
  { key: '{next_date}', meaning: 'the next follow-up date' },
  { key: '{person}',    meaning: 'the contact person' },
  { key: '{mobile}',    meaning: "the contact's number" },
  { key: '{user}',      meaning: 'your name' },
];

const SAMPLE = 'Dear {party}, our bill {bill} of Rs.{amount} is pending for {days} days. Kindly arrange the payment. — {user}, ABS Technologies';

const SmsTemplates: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { isAdmin } = useAuth();
  const canEdit = !!isAdmin?.();

  const [rows, setRows] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<Template | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', body: '', is_active: true });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Template | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await smsTemplateApi.getAll();
      setRows((res as any).data || []);
    } catch (e: any) {
      showError('Templates', e?.message || 'Could not load the templates');
    } finally { setLoading(false); }
  }, [showError]);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm({ name: '', body: SAMPLE, is_active: true }); setShowForm(true); };
  const openEdit = (t: Template) => { setEditing(t); setForm({ name: t.name, body: t.body, is_active: !!t.is_active }); setShowForm(true); };

  const save = async () => {
    if (!form.name.trim()) { showError('Validation', 'Template name is required'); return; }
    if (!form.body.trim()) { showError('Validation', 'Message is required'); return; }
    setSaving(true);
    try {
      if (editing) await smsTemplateApi.update(editing.id, form);
      else await smsTemplateApi.create(form);
      showSuccess('Saved', editing ? 'Template updated' : 'Template created');
      setShowForm(false);
      load();
    } catch (e: any) {
      showError('Could not save', e?.message || 'Failed');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    try {
      await smsTemplateApi.remove(deleteTarget.id);
      showSuccess('Deleted', `"${deleteTarget.name}" removed`);
      setDeleteTarget(null);
      load();
    } catch (e: any) {
      showError('Could not delete', e?.message || 'Failed');
    }
  };

  const insert = (token: string) => setForm(f => ({ ...f, body: `${f.body}${f.body.endsWith(' ') || !f.body ? '' : ' '}${token}` }));

  return (
    <div className="flex flex-col h-full bg-slate-50">
      <div className="flex-none flex items-center gap-2 px-3 py-2.5 bg-white border-b border-slate-200">
        <button onClick={() => navigate(-1)} className="p-1 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600"><ChevronLeft size={18} /></button>
        <MessageSquare size={16} className="text-blue-600" />
        <h1 className="text-[15px] font-bold text-slate-800">SMS Templates</h1>
        <span className="text-[11px] text-slate-500">{loading ? 'Loading…' : `${rows.length} template(s)`}</span>
        <button onClick={load} className="ml-auto p-1.5 rounded-lg text-slate-500 hover:bg-slate-100" title="Refresh"><RefreshCw size={15} /></button>
        {canEdit && (
          <button onClick={openAdd} className="inline-flex items-center gap-1 text-[12px] font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg px-3 py-1.5">
            <Plus size={14} /> New Template
          </button>
        )}
      </div>

      <div className="flex-1 overflow-auto p-4">
        <div className="max-w-4xl mx-auto space-y-3">
          {!loading && rows.length === 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-[13px] text-slate-500">
              No templates yet.{canEdit ? ' Create one and it will appear in the payment-followup screen.' : ' An administrator can add them.'}
            </div>
          )}

          {rows.map(t => (
            <div key={t.id} className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[14px] font-bold text-slate-800">{t.name}</span>
                    {!t.is_active && <span className="text-[10px] font-bold uppercase text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5">Off</span>}
                  </div>
                  <div className="text-[12.5px] text-slate-600 whitespace-pre-wrap mt-1">{t.body}</div>
                  {t.updated_by && <div className="text-[11px] text-slate-400 mt-1.5">last changed by {t.updated_by}</div>}
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1 flex-none">
                    <button onClick={() => openEdit(t)} className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50" title="Edit"><Pencil size={15} /></button>
                    <button onClick={() => setDeleteTarget(t)} className="p-1.5 rounded-lg text-red-500 hover:bg-red-50" title="Delete"><Trash2 size={15} /></button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Create / edit ── */}
      {showForm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-[15px] font-bold text-slate-800">{editing ? 'Edit Template' : 'New Template'}</h2>
              <button onClick={() => setShowForm(false)} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-[12px] font-semibold text-slate-600 mb-1">Template Name *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. First reminder"
                  className="w-full border border-slate-300 rounded-lg text-[13px] px-3 py-2 outline-none focus:ring-1 focus:ring-blue-400" autoFocus />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-slate-600 mb-1">SMS Details *</label>
                <textarea value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} rows={5}
                  placeholder="The message the customer receives"
                  className="w-full border border-slate-300 rounded-lg text-[13px] px-3 py-2 outline-none focus:ring-1 focus:ring-blue-400 resize-y" />
                <div className="text-[11px] text-slate-500 mt-1.5">{form.body.length}/2000 characters</div>
              </div>
              <div>
                <div className="text-[12px] font-semibold text-slate-600 mb-1.5">Placeholders — click to insert; the bill fills them in</div>
                <div className="flex flex-wrap gap-1.5">
                  {PLACEHOLDERS.map(p => (
                    <button key={p.key} type="button" onClick={() => insert(p.key)} title={p.meaning}
                      className="text-[11px] font-mono font-bold text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100 rounded-full px-2.5 py-1">
                      {p.key}
                    </button>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 text-[12.5px] text-slate-700">
                <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
                Offer this template on the payment-followup screen
              </label>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setShowForm(false)} className="text-[13px] font-semibold text-slate-600 px-4 py-2 rounded-lg hover:bg-slate-100">Cancel</button>
              <button onClick={save} disabled={saving}
                className="text-[13px] font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg px-4 py-2 disabled:opacity-50">
                {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete confirm ── */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5">
            <div className="text-[14px] font-bold text-slate-800 mb-1">Delete this template?</div>
            <div className="text-[12.5px] text-slate-600">"{deleteTarget.name}" will no longer be offered when following up a payment.</div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setDeleteTarget(null)} className="text-[13px] font-semibold text-slate-600 px-4 py-2 rounded-lg hover:bg-slate-100">Cancel</button>
              <button onClick={remove} className="text-[13px] font-bold text-white bg-red-600 hover:bg-red-700 rounded-lg px-4 py-2">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmsTemplates;
